import { Route, Switch } from "wouter";
import { HomeRoute } from "./routes/home/home";
import { SignInRoute } from "./routes/auth/signin";
import { AuthCallbackRoute } from "./routes/auth/callback";
import { SelectAccountRoute } from "./routes/auth/select-account";
import { SignoutRoute } from "./routes/auth/signout";
import { ProjectsRoute } from "./routes/projects/projects";
import { ProjectDetails } from "./routes/projects/project";
import { StudiesRoute } from "./routes/projects/studies";
import { StudyAssetContextRoute } from "./routes/studies/asset/study-asset-context";
import { StudyAssetSelectRoute } from "./routes/studies/asset/study-asset-select";
import { StudyAssetUploadRoute } from "./routes/studies/asset/study-asset-upload";
import { StudyAssetReviewRoute } from "./routes/studies/asset/study-asset-review";
import { StudyAssetProcessRoute } from "./routes/studies/asset/study-asset-process";
import { ReportsAttentionOverviewRoute } from "./routes/reports/asset/attention/reports-overview";
import { ReportInDepthRoute } from "./routes/reports/asset/report-in-depth";
import { ContextsRoute } from "./routes/contexts/contexts";
import { StudyAssetContextPackUploadRoute } from "./routes/studies/asset/study-asset-context-pack-upload";
import { StudyAssetContextPackDropzoneRoute } from "./routes/studies/asset/study-asset-context-pack-dropzone";
import { StudyAssetContextPackSelectRoute } from "./routes/studies/asset/study-asset-context-pack-select";
import { ContextPackRoute } from "./routes/contexts/context-pack";
import { LibraryContextPackSelectRoute } from "./routes/contexts/library-context-pack-select";
import { LibraryContextPackUploadRoute } from "./routes/contexts/library-context-pack-upload";
import { LibraryContextPackDropzoneRoute } from "./routes/contexts/library-context-pack-dropzone";
import { ReportsErrorBoundary } from "@/components/fallbacks/error-boundaries";
import { HomeLayout } from "@/components/layouts";
import { PageNotFound } from "@/components/fallbacks/page-not-found";
import { ReportsCreativeOverviewRoute } from "./routes/reports/asset/reports-overview";
import { ReportsEmotionRoute } from "./routes/reports/asset/emotion/reports-emotion";
import { ReportsMemoryRoute } from "./routes/reports/asset/memory/reports-memory";
import { ReportsProvider } from "@/features/reports/contexts/reports-context";
import { StudySceneSelectRoute } from "./routes/studies/scene/study-scene-select";
import { StudySceneUploadRoute } from "./routes/studies/scene/study-scene-upload";
import { StudySceneReviewRoute } from "./routes/studies/scene/study-scene-review";
import { StudySceneProcessRoute } from "./routes/studies/scene/study-scene-process";
import { ReportsSceneOverviewRoute } from "./routes/reports/scene/reports-scene-overview";
import { ReportsSceneInDepthRoute } from "./routes/reports/scene/reports-scene-in-depth";

/**
 * PublicRoutes component
 *
 * Defines all the public routes for the application using wouter. These routes
 * are accessible without authentication and are typically related to signing in
 * or signing out.
 */
export const PublicRoutes = () => {
  return (
    <Switch>
      {/* Auth */}
      <Route path="/auth/signin" component={SignInRoute} />
      <Route path="/auth/callback" component={AuthCallbackRoute} />
      <Route path="/auth/select-account" component={SelectAccountRoute} />
      <Route path="/auth/signout" component={SignoutRoute} />

      {/* 404 */}
      <Route path="*" component={PageNotFound} />
    </Switch>
  );
};

/*
 * AppRoutes component
 *
 * Defines all the routes for the application using wouter.
 */
export const AppRoutes = () => {
  /* Render */
  return (
    <Switch>
      {/* Auth */}
      <Route path="/auth/signin" component={SignInRoute} />
      <Route path="/auth/callback" component={AuthCallbackRoute} />
      <Route path="/auth/select-account" component={SelectAccountRoute} />
      <Route path="/auth/signout" component={SignoutRoute} />

      {/* Home */}
      <Route path="/" component={HomeRoute} />

      {/* Projects */}
      <Route path="/projects" component={ProjectsRoute} />
      <Route path="/projects/:id">{(params) => <ProjectDetails id={params.id} />}</Route>
      <Route path="/studies" component={StudiesRoute} />

      {/* Contexts */}
      <Route path="/contexts" component={ContextsRoute} />
      <Route path="/contexts/:contextPackId">{(params) => <ContextPackRoute contextPackId={params.contextPackId} />}</Route>
      <Route path="/contexts/:contextPackId/context-select">{(params) => <LibraryContextPackSelectRoute contextPackId={params.contextPackId} />}</Route>
      <Route path="/contexts/:contextPackId/context-upload">{(params) => <LibraryContextPackUploadRoute contextPackId={params.contextPackId} />}</Route>
      <Route path="/contexts/:contextPackId/context-dropzone">{(params) => <LibraryContextPackDropzoneRoute contextPackId={params.contextPackId} />}</Route>

      {/* Asset Study */}
      <Route path="/projects/:projectId/studies/:studyId/asset/study-select">{(params) => <StudyAssetSelectRoute projectId={params.projectId} studyId={params.studyId} />}</Route>
      <Route path="/projects/:projectId/studies/:studyId/asset/study-upload">{(params) => <StudyAssetUploadRoute projectId={params.projectId} studyId={params.studyId} />}</Route>
      <Route path="/projects/:projectId/studies/:studyId/asset/study-review">{(params) => <StudyAssetReviewRoute projectId={params.projectId} studyId={params.studyId} />}</Route>
      <Route path="/projects/:projectId/studies/:studyId/asset/study-process">{(params) => <StudyAssetProcessRoute projectId={params.projectId} studyId={params.studyId} />}</Route>
      <Route path="/projects/:projectId/studies/:studyId/asset/study-context">{(params) => <StudyAssetContextRoute projectId={params.projectId} studyId={params.studyId} />}</Route>
      <Route path="/projects/:projectId/studies/:studyId/asset/study-context/:contextPackId/select">
        {(params) => <StudyAssetContextPackSelectRoute projectId={params.projectId} studyId={params.studyId} contextPackId={params.contextPackId} />}
      </Route>
      <Route path="/projects/:projectId/studies/:studyId/asset/study-context/:contextPackId/upload">
        {(params) => <StudyAssetContextPackUploadRoute projectId={params.projectId} studyId={params.studyId} contextPackId={params.contextPackId} />}
      </Route>
      <Route path="/projects/:projectId/studies/:studyId/asset/study-context/:contextPackId/dropzone">
        {(params) => <StudyAssetContextPackDropzoneRoute projectId={params.projectId} studyId={params.studyId} contextPackId={params.contextPackId} />}
      </Route>

      {/* Scene Study */}
      <Route path="/projects/:projectId/studies/:studyId/scene/study-select">{(params) => <StudySceneSelectRoute projectId={params.projectId} studyId={params.studyId} />}</Route>
      <Route path="/projects/:projectId/studies/:studyId/scene/study-upload">{(params) => <StudySceneUploadRoute projectId={params.projectId} studyId={params.studyId} />}</Route>
      <Route path="/projects/:projectId/studies/:studyId/scene/study-review">{(params) => <StudySceneReviewRoute projectId={params.projectId} studyId={params.studyId} />}</Route>
      <Route path="/projects/:projectId/studies/:studyId/scene/study-process">{(params) => <StudySceneProcessRoute projectId={params.projectId} studyId={params.studyId} />}</Route>

      {/* Asset Reports */}
      <ReportsProvider>
        <Route path="/projects/:projectId/studies/:studyId/asset/reports/overview">
          {(params) => (
            <ReportsErrorBoundary studyId={params.studyId} projectId={params.projectId}>
              <ReportsCreativeOverviewRoute projectId={params.projectId} studyId={params.studyId} />
            </ReportsErrorBoundary>
          )}
        </Route>
        <Route path="/projects/:projectId/studies/:studyId/asset/reports/emotion/overview">
          {(params) => (
            <ReportsErrorBoundary studyId={params.studyId} projectId={params.projectId}>
              <ReportsEmotionRoute projectId={params.projectId} studyId={params.studyId} />
            </ReportsErrorBoundary>
          )}
        </Route>
        <Route path="/projects/:projectId/studies/:studyId/asset/reports/memory/overview">
          {(params) => (
            <ReportsErrorBoundary studyId={params.studyId} projectId={params.projectId}>
              <ReportsMemoryRoute projectId={params.projectId} studyId={params.studyId} />
            </ReportsErrorBoundary>
          )}
        </Route>
        <Route path="/projects/:projectId/studies/:studyId/asset/reports/attention/overview">
          {(params) => (
            <ReportsErrorBoundary studyId={params.studyId} projectId={params.projectId}>
              <ReportsAttentionOverviewRoute projectId={params.projectId} studyId={params.studyId} />
            </ReportsErrorBoundary>
          )}
        </Route>
        {/* Report In-Depth */}
        <Route path="/projects/:projectId/studies/:studyId/asset/reports/in-depth">
          {(params) => (
            <ReportsErrorBoundary studyId={params.studyId} projectId={params.projectId}>
              <ReportInDepthRoute projectId={params.projectId} studyId={params.studyId} />
            </ReportsErrorBoundary>
          )}
        </Route>

        {/* Scene Reports */}
        <Route path="/projects/:projectId/studies/:studyId/scene/reports/overview">
          {(params) => (
            <ReportsErrorBoundary studyId={params.studyId} projectId={params.projectId} studyType="scene">
              <ReportsSceneOverviewRoute projectId={params.projectId} studyId={params.studyId} />
            </ReportsErrorBoundary>
          )}
        </Route>
        <Route path="/projects/:projectId/studies/:studyId/scene/reports/in-depth/:sceneKey">
          {(params) => (
            <ReportsErrorBoundary studyId={params.studyId} projectId={params.projectId} studyType="scene">
              <ReportsSceneInDepthRoute projectId={params.projectId} studyId={params.studyId} sceneKey={params.sceneKey} />
            </ReportsErrorBoundary>
          )}
        </Route>
      </ReportsProvider>

      {/* 404 */}
      <Route path="*">
        <HomeLayout>
          <PageNotFound />
        </HomeLayout>
      </Route>
    </Switch>
  );
};
